import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Container, Form, Spinner } from "reactstrap";
import { Stack, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { TableC } from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { SelectCountry } from "@/Component/Form/SelectCountry/SelectCountry";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { usePagination } from "@/utils/hooks/usePagination";
import {
  useGroupStatementReportMutation,
  useGroupStatementReportQuery,
} from "@/services/reports";
import StatementReportFilters from "./StatementReportFilters";
import { useSystem } from "@/utils/hooks/useLocation";
import { SYSTEMS } from "@/utils/permissions";

const PAGE_SIZE = 10;

const pickText = (...values) =>
  values.find((value) => value != null && value !== "") ?? "-";

const pickNumber = (...values) => {
  const raw = values.find((value) => value != null && value !== "");
  if (raw == null || raw === "") return "-";
  const number = Number(raw);
  return Number.isFinite(number) ? number.toLocaleString() : raw;
};

const translateOrFallback = (t, key, fallback) => {
  const translated = t(key);
  return translated === key ? fallback : translated;
};

const GroupStatementReport = () => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const [reportDataRes, setReportDataRes] = useState(null);
  const [shouldExport, setShouldExport] = useState(false);
  const [exportPayload, setExportPayload] = useState(null);
  const tableRef = useRef(null);
  const {currentSystem}=useSystem();
  const directionId = currentSystem === SYSTEMS.MAKKAH.code? SYSTEMS.MAKKAH.id : SYSTEMS.MADINA.id;
  const reportRows = reportDataRes?.data?.data || [];
  const meta = reportDataRes?.data?.meta || {};
  const totals = meta?.totals || {};

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      format: "json",
      orientation: "landscape",
      group_number: "",
      organizer_country: "",
      nationality: "",
      missions_id: "",
      service_center_id: "",
      madina_service_center_id: "",
      direction_id: directionId,
    },
  });

  const format = watch("format");
  const isPdf = format === "pdf";

  const { mutate: generateReport, isPending: isGenerating } =
    useGroupStatementReportMutation();

  const {
    isFetching: isExporting,
    isSuccess: isExportSuccess,
    isError: isExportError,
    error: exportError,
  } = useGroupStatementReportQuery(exportPayload, shouldExport);

  useEffect(() => {
    if (isExportSuccess) {
      toast.success(t("REPORT_EXPORTED_SUCCESSFULLY") || "Report exported successfully");
      setShouldExport(false);
      setExportPayload(null);
    }

    if (isExportError) {
      toast.error(
        exportError?.response?.data?.message ||
          t("REPORT_EXPORT_FAILED") ||
          "Failed to export report"
      );
      setShouldExport(false);
      setExportPayload(null);
    }
  }, [exportError, isExportError, isExportSuccess, t]);

  const fetchReportData = (payload, pageNum = 1) => {
    generateReport(
      {
        ...payload,
        page: pageNum,
        per_page: PAGE_SIZE,
      },
      {
        onSuccess: (response) => {
          setReportDataRes(response);
          setTimeout(() => {
            tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        },
      }
    );
  };

  const {
    page,
    setPage,
    hasSearched,
    handleSearch,
    handleReset: resetPagination,
  } = usePagination(fetchReportData, PAGE_SIZE);

  const onSubmit = (data) => {
    if (data.format === "json") {
      handleSearch(data);
      return;
    }

    setExportPayload(data);
    setShouldExport(true);
  };

  const columns = useMemo(
    () => [
      {
        name: t("GROUP_NUMBER"),
        key: "group_number",
        render: (_, row) => pickText(row.group_number),
      },
      {
        name: translateOrFallback(t, "ORGANIZER_COUNTRY", "Organizer Country"),
        key: "organizer_country",
        render: (value) =><SelectCountry.Display value={value} />,
      },
      {
        name: t("ORGANIZER_NAME"),
        key: "organizer_name",
        render: (_, row) =>
          pickText(
            row.organizer_name,
            row.delegation_name,
            getLocalizedText(row.delegation_detail?.name_ar, row.delegation_detail?.name_en),
            getLocalizedText(row.delegation_detail?.nameAr, row.delegation_detail?.nameEn),
            getLocalizedText(row.mission?.name_ar, row.mission?.name_en),
            getLocalizedText(row.mission?.nameAr, row.mission?.nameEn),
            row.mission?.name
          ),
      },
      {
        name: t("GROUP_NAME"),
        key: "display_name",
        render: (_, row) => {
          const baseName = pickText(row.display_name, row.group_name, row.name);
          return row.groups_suffix && baseName !== "-"
            ? `${baseName} ${row.groups_suffix}`
            : baseName;
        },
      },
      {
        name: t("NATIONALITY"),
        key: "nationality",
        render: (value) =><SelectCountry.Display value={value} />,
      },
      {
        name: translateOrFallback(t, "MAKKAH_SERVICE_CENTER_ID", "Service Center"),
        key: "service_center_makkah",
        render: (_, row) =>
          pickText(
            row.service_center_makkah,
            row.service_center_name,
            getLocalizedText(row.service_center?.name_ar, row.service_center?.name_en),
            getLocalizedText(row.service_center?.nameAr, row.service_center?.nameEn),
            row.service_center?.name
          ),
      },
      {
        name: translateOrFallback(
          t,
          "MADINA_SERVICE_CENTER_ID",
          "Service Center in Madina"
        ),
        key: "service_center_madina",
        render: (_, row) => pickText(row.service_center_madina),
      },
      {
        name: t("TOTAL_PILGRIMS"),
        key: "total_pilgrims",
        render: (_, row) =>
          pickNumber(row.total_pilgrims, row.capacity, row.pilgrims_count),
      },
      {
        name: translateOrFallback(t, "HOUSING_CONTRACT", "Housing Contract"),
        key: "contracts_count",
        render: (_, row) =>
          pickText(row.contracts_summary, row.contracts_count, row.details_count, row.contract_count),
      },
    ],
    [getLocalizedText, t]
  );

  const footerTotalPilgrims =
    totals.total_pilgrims ??
    reportRows.reduce(
      (sum, row) =>
        sum + Number(row.total_pilgrims || row.capacity || row.pilgrims_count || 0),
      0
    );

  const isLoading = isGenerating || isExporting;

  const handleReset = () => {
    reset();
    resetPagination();
    setReportDataRes(null);
  };

  return (
    <Stack gap={3}>
      <Container fluid>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <StatementReportFilters
            control={control}
            errors={errors}
            type="group"
            isPdf={isPdf}
          />
          <ActionsFooter>
            <Button variant="primary" type="submit" disabled={isLoading} className="mt-3">
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t("LOADING") || "Loading..."}
                </>
              ) : format === "json" ? (
                t("VIEW_REPORTS")
              ) : format === "pdf" ? (
                t("EXPORT_PDF")
              ) : (
                t("EXPORT_EXCEL")
              )}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={handleReset}
              disabled={isLoading}
              className="mt-3"
            >
              {t("RESET") || "Reset"}
            </Button>
          </ActionsFooter>
        </Form>
      </Container>

      {(hasSearched || reportRows.length > 0) && format === "json" && (
        <Container fluid>
          <TableC
            ref={tableRef}
            data={reportRows}
            columns={columns}
            loading={isGenerating}
            emptyStateTitle={t("NO_DATA") || "No data"}
            emptyStateDescription={t("NO_DATA_DESCRIPTION") || "No data found"}
            pagination={{
              current: page,
              total: meta?.total ?? 0,
              pageSize: meta?.per_page ?? PAGE_SIZE,
              onChange: setPage,
            }}
          />
          {reportRows.length > 0 && (
            <div className="fw-bold py-2 px-3 bg-white border rounded-bottom">
              {t("TOTAL_PILGRIMS")}: {pickNumber(footerTotalPilgrims)}
            </div>
          )}
        </Container>
      )}
    </Stack>
  );
};

export default GroupStatementReport;
